import {
  applyToPoint,
  boundsOfPoints,
  outlineStroke,
  sweptRings,
  closedUp,
  erasedAt,
  invert,
  joined,
  multiply,
  ROOT_ID,
  recogniseStroke,
  simplifyStroke,
  smoothedAt,
  strokeToSubpath,
  type Guess,
  type Matrix,
  type PathPoint,
  type StrokePoint,
  type Subpath, placeInParent } from '@flobi/artwork-core';
import { styleFill, styleStroke } from '../colors';
import type { IconName } from '../chrome/icons';
import type { Point, RailButton, Tool, ToolContext } from './types';

/**
 * Drawing by hand, with Procreate's QuickShape.
 *
 * Draw a shape roughly and let go and you keep what you drew. Draw it and *hold* at the
 * end, and the stroke is read — a line, a circle, a rectangle, a polygon — and replaced
 * by the clean thing you were aiming at. The holding is what makes it work: it is the
 * difference between a drawing and a request, and it means nothing is ever straightened
 * against the hand's wishes.
 *
 * Nothing is written to the document until the stroke ends. A freehand line is hundreds
 * of points, and writing the whole growing array on every sample would cost the document
 * — and everyone syncing with it — far more than the second it takes to draw.
 */

/**
 * What a press of the pencil means.
 *
 * The panel groups these six modes into creation and path editing. Three modes
 * draw new geometry — shape assist, pencil and fill brush — and three
 * work on a line already there: soften it, rub part of it out, join two loose ends. They
 * belong together because they are the same hand doing the same gesture over the same
 * kind of thing, which is exactly what a flyout in Illustrator means too.
 */
type DrawMode = 'shape' | 'pencil' | 'blob' | 'smooth' | 'erase' | 'join';

const MODES: { id: DrawMode; icon: IconName; title: string; panelGroup: string }[] = [
  { id: 'shape', icon: 'magic', panelGroup: 'Create', title: 'Shape assist — Draw a rough shape, then hold to straighten it.' },
  { id: 'pencil', icon: 'draw', panelGroup: 'Create', title: 'Pencil — Draw a freehand path.' },
  { id: 'blob', icon: 'brush', panelGroup: 'Create', title: 'Fill brush — Paint filled shapes that merge as they touch.' },
  { id: 'smooth', icon: 'smooth', panelGroup: 'Edit paths', title: 'Smooth — Brush over a path to soften its curves.' },
  { id: 'erase', icon: 'eraser', panelGroup: 'Edit paths', title: 'Erase — Brush over a path to remove sections.' },
  { id: 'join', icon: 'join', panelGroup: 'Edit paths', title: 'Join — Click between two open ends to connect them.' },
];

/** How long the hand must hold still, and how still, before the stroke is read. */
const HOLD_MS = 450;
const HOLD_SLOP = 4;
/** Samples nearer than this to the last one say nothing, so they are dropped. */
const MIN_STEP = 1.5;
/** How near a press has to be to a loose end to take hold of it, in screen pixels. */
const JOIN_REACH = 24;

export function drawTool(): Tool {
  /** How hard the thinning is, and how wide the reshaping brush reaches. */
  const settings = { smoothing: 1.2, brush: 20 };
  /** Which of the six modes a press means. Kept across tool changes, as a mode should be. */
  let mode: DrawMode = 'shape';
  let points: StrokePoint[] = [];
  let drawing = false;
  /** A smoothing or rubbing drag, which works on lines already there rather than making one. */
  let reshaping = false;
  /** Where the pointer is with nothing pressed, so the brush can be shown at its size. */
  let hover: Point | null = null;
  let snapped = false;
  /** What a hold-snap put on the page, so Escape before letting go can take it back. */
  let snappedId: string | null = null;
  let hold: ReturnType<typeof setTimeout> | null = null;
  /**
   * The Fill brush's preview, worked out once per new sample rather than every frame: the
   * rings wound one way, so they wash in as one band (pencil-fill-brush-F19).
   */
  let band: { count: number; zoom: number; brush: number; rings: Point[][] } | null = null;

  const stopHold = () => {
    if (hold !== null) clearTimeout(hold);
    hold = null;
  };

  const reset = () => {
    stopHold();
    points = [];
    drawing = false;
    reshaping = false;
    snapped = false;
    snappedId = null;
    band = null;
  };

  /**
   * Ends a stroke the way its own mode ends it: the Fill brush leaves paint, a stroke that
   * already snapped adds nothing, anything else is kept as drawn. Answers whether anything
   * was written, which is what closes the undo step — guessing from the number of samples
   * left a short dab open to merge with the next edit (QA 2026-09-25, pencil-fill-brush-F8, F9).
   */
  const finishStroke = (ctx: ToolContext): boolean => {
    if (!drawing) return false;
    if (mode === 'blob') return blob(ctx, points, (settings.brush / 2) * ctx.unitsPerPixel, settings.smoothing * ctx.unitsPerPixel);
    if (snapped) return true;
    return points.length > 2 && commit(ctx, { kind: 'freehand' }, points, settings.smoothing) !== null;
  };

  /** A node's subpaths, when it is a path at all. */
  const subpathsOf = (ctx: ToolContext, id: string): Subpath[] | null => {
    const node = ctx.node(id);
    if (node?.type !== 'path') return null;
    return (node.path?.subpaths ?? []) as Subpath[];
  };

  /**
   * Every path the brush is over, with the way into and out of each one's own coordinates.
   *
   * The reshaping modes all want the same thing — which lines am I touching, and where am
   * I touching them — so they ask once, here, and differ only in what they then do.
   *
   * The reach is exactly the circle drawn under the pointer: HALF the brush size, since
   * the slider sets its width. It used to be the whole size, so lines twice as far away as
   * the circle were changed, and the press point was carried into the node's coordinates
   * while the radius stayed in the page's — so a scaled drawing was reached from farther
   * still (QA 2026-09-25, pencil-fill-brush-F1). Now each line is carried out onto the
   * page and measured against the circle there, which is right for any scale, rotation
   * or squash, and the answer is carried back.
   */
  const under = (ctx: ToolContext, world: Point) => {
    const radius = (settings.brush / 2) * ctx.unitsPerPixel;
    const box = { x: world.x - radius, y: world.y - radius, width: radius * 2, height: radius * 2 };
    const found: { id: string; toWorld: Matrix; back: Matrix; subpaths: Subpath[] }[] = [];
    for (const id of ctx.nearby(box)) {
      const node = ctx.node(id);
      // Not a line inside a locked or hidden group either: nothing else can reach those.
      if (node?.type !== 'path' || !ctx.scene.choosable(id)) continue;
      const toWorld = ctx.scene.worldTransform(id);
      const back = invert(toWorld);
      const subpaths = node.path?.subpaths;
      if (!back || !subpaths?.length) continue;
      found.push({ id, toWorld, back, subpaths: subpaths as Subpath[] });
    }
    return { radius, found };
  };

  /**
   * Softens whatever the brush is over, a little per frame of the drag.
   *
   * On the page's own scale, so the brush eases what is inside its circle and nothing
   * else; only the anchors it moved are carried back, so the rest stay exactly as stored.
   */
  const soften = (ctx: ToolContext, world: Point) => {
    const { radius, found } = under(ctx, world);
    for (const path of found) {
      let changed = false;
      const next = path.subpaths.map((sub) => {
        const onPage = mapped(sub.points, path.toWorld);
        const eased = smoothedAt(onPage, sub.closed, world, radius);
        const points = eased.map((point, index) => {
          if (point === onPage[index]) return sub.points[index]!;
          changed = true;
          return mapped([point], path.back)[0]!;
        });
        return { closed: sub.closed, points };
      });
      if (changed) ctx.edit([{ op: 'set', id: path.id, props: { path: { subpaths: next } } }]);
    }
  };

  /**
   * Rubs out the stretch of line under the brush, and takes the node with it if nothing
   * is left. The line itself is cut where it crosses the circle (pencil-fill-brush-N5),
   * so a Pen path loses only what the brush went over.
   */
  const rub = (ctx: ToolContext, world: Point) => {
    const { radius, found } = under(ctx, world);
    for (const path of found) {
      let changed = false;
      const next = path.subpaths.flatMap((sub) => {
        const onPage = mapped(sub.points, path.toWorld);
        const left = erasedAt(onPage, sub.closed, world, radius);
        // The same array back means the brush missed this subpath: keep it as stored.
        if (left.length === 1 && left[0]!.points === onPage) return [sub];
        changed = true;
        return left.map((piece) => ({ closed: piece.closed, points: mapped(piece.points, path.back) }));
      });
      if (!changed) continue;
      if (next.length === 0) {
        ctx.edit([{ op: 'delete', id: path.id }]);
        continue;
      }
      ctx.edit([{ op: 'set', id: path.id, props: { path: { subpaths: next } } }]);
    }
  };

  /**
   * Joins the two loose ends nearest a press.
   *
   * Two ends of the same line close it into a ring; two ends of different lines make one
   * line, and when those lines are different NODES the second's points are carried into
   * the first's coordinates before they are added, so the joined line is one shape rather
   * than two halves that happen to touch on screen.
   *
   * Which line survives — and so whose colour, weight and place the joined line keeps —
   * is a stated rule, not whichever end was a hair nearer the press: the SELECTED line if
   * exactly one of the two is, and otherwise the one on top. And two lines on different
   * artboards are not joined at all: artboards clip what is in them, so the joined line
   * would vanish wherever it ran over the other board (QA 2026-09-25, pencil-fill-brush-N3).
   */
  const join = (ctx: ToolContext, world: Point): boolean => {
    // A fixed reach rather than the brush's: joining is a hit test, not a sweep. Ends far
    // enough apart to need a wide brush are not a gap being closed, they are two lines
    // somebody would rather move first.
    const reach = JOIN_REACH * ctx.unitsPerPixel;
    const box = { x: world.x - reach, y: world.y - reach, width: reach * 2, height: reach * 2 };
    const loose: { id: string; index: number; away: number; sub: Subpath }[] = [];
    for (const id of ctx.nearby(box)) {
      const node = ctx.node(id);
      if (node?.type !== 'path' || !ctx.scene.choosable(id)) continue;
      const toWorld = ctx.scene.worldTransform(id);
      (node.path?.subpaths ?? []).forEach((sub, index) => {
        if (sub.closed || sub.points.length === 0) return;
        // Measured on the page, like the reach it is compared with. It used to be measured
        // in the node's own units against a reach in the page's, so a scaled line was
        // caught from too far or not at all (pencil-fill-brush-F1).
        const ends = [sub.points[0]!, sub.points[sub.points.length - 1]!].map((end) => applyToPoint(toWorld, end.x, end.y));
        const away = Math.min(...ends.map((end) => Math.hypot(end.x - world.x, end.y - world.y)));
        if (away <= reach) loose.push({ id, index, away, sub: sub as Subpath });
      });
    }
    if (loose.length === 0) return false;
    loose.sort((a, b) => a.away - b.away);
    let [first, second] = loose;

    // One loose end, or both ends of the same line: the line closes on itself.
    if (!second || (second.id === first!.id && second.index === first!.index)) {
      const subpaths = subpathsOf(ctx, first!.id);
      if (!subpaths) return false;
      const next = [...subpaths];
      next[first!.index] = closedUp(first!.sub);
      ctx.edit([{ op: 'set', id: first!.id, props: { path: { subpaths: next } } }]);
      return true;
    }

    if (first!.id !== second.id) {
      if (artboardOf(ctx, first!.id) !== artboardOf(ctx, second.id)) {
        ctx.notice('These lines are on different artboards. Move them onto the same one to join them.');
        return false;
      }
      // The survivor: the selected one, or else the one painted on top.
      const chosen = (id: string) => ctx.selection.includes(id);
      const order = ctx.scene.paintOrder();
      const keepSecond =
        chosen(first!.id) !== chosen(second.id) ? chosen(second.id) : order.indexOf(second.id) > order.indexOf(first!.id);
      if (keepSecond) [first, second] = [second, first!];
    }

    const back = invert(ctx.scene.worldTransform(first!.id));
    const subpaths = subpathsOf(ctx, first!.id);
    if (!back || !subpaths) return false;
    // The second line's points carried into the first's coordinates, so what comes out is
    // one shape rather than two halves that happen to touch on the screen.
    const carried: Subpath =
      first!.id === second!.id
        ? second!.sub
        : { closed: false, points: mapped(mapped(second!.sub.points, ctx.scene.worldTransform(second!.id)), back) };

    const made = joined(first!.sub, carried);
    if (!made) return false;
    const next = [...subpaths];
    next[first!.index] = made;
    if (first!.id === second!.id) {
      next.splice(second!.index, 1);
      ctx.edit([{ op: 'set', id: first!.id, props: { path: { subpaths: next } } }]);
      return true;
    }
    const left = (subpathsOf(ctx, second!.id) ?? []).filter((_, index) => index !== second!.index);
    ctx.edit([
      { op: 'set', id: first!.id, props: { path: { subpaths: next } } },
      ...(left.length > 0
        ? [{ op: 'set', id: second!.id, props: { path: { subpaths: left } } }]
        : [{ op: 'delete', id: second!.id }]),
    ]);
    return true;
  };

  return {
    id: 'draw',

    deactivate(ctx) {
      hover = null;
      // Leaving mid-stroke keeps what was drawn, rather than losing it to a stray tap on
      // another tool — in the stroke's own mode: a Fill brush stroke used to come out as a
      // thin pencil line, and a snapped one got a scribble on top (pencil-fill-brush-F8).
      if (finishStroke(ctx)) ctx.stopCapturing();
      reset();
      ctx.refresh();
    },

    /**
     * The system took the press away, or a second finger landed: the stroke is dropped,
     * not saved. What a snap already put down stays, as its own undo step
     * (QA 2026-09-25, pencil-fill-brush-F8, F14).
     */
    onPointerCancel(ctx) {
      if (snapped) ctx.stopCapturing();
      reset();
      ctx.refresh();
    },

    /** The brush circle goes with the hand (pencil-fill-brush-F6). */
    onPointerLeave(ctx) {
      if (drawing || reshaping || !hover) return;
      hover = null;
      ctx.refresh();
    },

    onPointerDown(event, world, ctx) {
      if (event.button !== 0) return false;
      // Nothing carries over from the last press. A gesture that ended somewhere this
      // tool never heard about — the pointer left the window, the tool changed under it —
      // would otherwise leave a flag set, and the next press would be swallowed by a
      // gesture nobody is making.
      reshaping = false;
      if (mode === 'join') {
        if (join(ctx, world)) ctx.stopCapturing();
        return true;
      }
      if (mode === 'smooth' || mode === 'erase') {
        reshaping = true;
        if (mode === 'smooth') soften(ctx, world);
        else rub(ctx, world);
        return true;
      }
      // The fill brush falls through to the drawing below: it is painted with the same
      // gesture as the pencil and differs only in what is left behind at the end. It
      // always paints with the fill colour, so with Fill set to None the well is turned
      // back on and shows the colour it is painting with, rather than saying None while
      // colour goes down (QA 2026-09-25, pencil-fill-brush-F5).
      if (mode === 'blob' && !ctx.style.fillOn) ctx.drawWith({ fillOn: true });
      reset();
      drawing = true;
      points = [world];
      ctx.refresh();
      return true;
    },

    onPointerMove(event, world, ctx) {
      // Every move, pressed or not: the circle stayed where an Erase or Smooth drag began,
      // and came back at a Fill-brush stroke's start after it (pencil-fill-brush-F6).
      hover = world;
      if (event.buttons === 0 || reshaping) ctx.refresh();
      if (reshaping) {
        if (event.buttons === 0) return;
        if (mode === 'smooth') soften(ctx, world);
        else rub(ctx, world);
        return;
      }
      if (!drawing || snapped || event.buttons === 0) return;
      const last = points[points.length - 1];
      if (last && Math.hypot(world.x - last.x, world.y - last.y) < MIN_STEP * ctx.unitsPerPixel) return;
      points.push(world);
      ctx.refresh();

      // Every movement puts the reading off; stillness is what asks for it.
      stopHold();
      // Only the shaper reads a stroke. The pencil and the fill brush are the same
      // gesture with the reading turned off — which is the whole of the difference
      // between the pencil and the shaper in Illustrator too.
      if (mode !== 'shape' || points.length < 4) return;
      const held = world;
      hold = setTimeout(() => {
        hold = null;
        const now = points[points.length - 1]!;
        if (!drawing || Math.hypot(now.x - held.x, now.y - held.y) > HOLD_SLOP * ctx.unitsPerPixel) return;
        const guess = recogniseStroke(points);
        if (guess.kind === 'freehand') return;
        snapped = true;
        snappedId = commit(ctx, guess, points, settings.smoothing);
        ctx.refresh();
      }, HOLD_MS);
    },

    onPointerUp(_event, _world, ctx) {
      if (reshaping) {
        reshaping = false;
        ctx.stopCapturing();
        return;
      }
      stopHold();
      if (!drawing) return;
      // A stroke that already snapped is on the page; one that did not is kept as drawn,
      // because letting go is how you say "I meant that". A still click with the Fill
      // brush is a round dab of the brush's size — it used to leave nothing unless the hand
      // happened to wiggle (QA 2026-09-25, pencil-fill-brush-F7). Whatever was written is
      // its own undo step (pencil-fill-brush-F9).
      const wrote = finishStroke(ctx);
      reset();
      ctx.refresh();
      if (wrote) ctx.stopCapturing();
    },

    onKeyDown(event, ctx) {
      if (event.key !== 'Escape' || !drawing) return false;
      // Handled here, so the page does not also close Layers (pencil-fill-brush-F12).
      event.preventDefault();
      // Escape before letting go takes a snapped shape back too: it stayed, and merged with
      // the next edit's undo step (QA 2026-09-25, pencil-fill-brush-F12).
      if (snapped) {
        if (snappedId && ctx.node(snappedId)) {
          ctx.edit([{ op: 'delete', id: snappedId }]);
          ctx.setSelection([]);
        }
        ctx.stopCapturing();
      }
      reset();
      ctx.refresh();
      return true;
    },

    /**
     * The six ways of using it above, and the one quantity the mode in hand has below.
     *
     * Drawing is thinned by a smoothing; brushing is sized by a brush. Joining is neither
     * — it happens where you press or it does not — so it shows no well at all, and the
     * style block goes with it: a join keeps the look of one of the lines it joins (the
     * selected one, or else the upper one) and never paints anew.
     */
    rail(ctx) {
      const items: RailButton[] = MODES.map((item) => ({
        id: item.id,
        icon: item.icon,
        title: item.title,
        panelGroup: item.panelGroup,
        pressed: mode === item.id,
        run: () => {
          mode = item.id;
          ctx.railChanged();
        },
      }));
      const top = { kind: 'buttons' as const, items };
      if (mode === 'join') return { top };
      if (mode === 'blob' || mode === 'smooth' || mode === 'erase') {
        return {
          top,
          bottom: {
            kind: 'slider' as const,
            icon: 'opacity' as const,
            title: 'Brush size',
            value: settings.brush,
            min: 4,
            max: 120,
            onChange: (value: number) => {
              settings.brush = value;
              ctx.railChanged();
            },
          },
          // The blob paints with the fill, so it needs the swatches; the reshaping two
          // change a line's shape and never its colour, so they do not.
          ...(mode === 'blob' ? { style: true } : {}),
        };
      }
      return {
        top,
        bottom: {
          kind: 'slider' as const,
          icon: 'draw' as const,
          title: 'Smoothing',
          value: settings.smoothing,
          min: 0.2,
          max: 4,
          step: 0.1,
          onChange: (value: number) => {
            settings.smoothing = value;
            ctx.railChanged();
          },
        },
        style: true,
      };
    },

    /**
     * What the hand is doing, while it is doing it.
     *
     * The pencil shows the line it is laying down. The fill brush shows the EDGE of the
     * shape it is about to leave, because the thing it makes is a shape and a line down
     * the middle of one says nothing about how wide it will be — you would be painting
     * blind until you let go. With nothing pressed, the brush itself is drawn where the
     * pointer is, so its size is something you can see before you commit to it rather
     * than a number on a slider you have to translate.
     */
    draw(overlay, ctx) {
      const wide = (settings.brush / 2) * ctx.unitsPerPixel;
      if (mode === 'blob' && drawing) {
        // One band the width of the brush, washed in as the shape it will leave, rather
        // than every circle and bar of the sweep outlined on top of each other — and only
        // worked out again when the stroke or the zoom has changed (pencil-fill-brush-F19).
        // Thinned exactly as blob() will thin it, to the zoom (pencil-fill-brush-F13).
        if (!band || band.count !== points.length || band.zoom !== ctx.unitsPerPixel || band.brush !== settings.brush) {
          const spine = simplifyStroke(points, settings.smoothing * ctx.unitsPerPixel * 0.8);
          band = { count: points.length, zoom: ctx.unitsPerPixel, brush: settings.brush, rings: sweptRings(spine, wide).map(woundOneWay) };
        }
        overlay.region(band.rings, { alpha: 0.35 });
        return;
      }
      if ((mode === 'blob' || mode === 'smooth' || mode === 'erase') && hover && !drawing) {
        overlay.curve(circleAt(hover, wide).map((at) => ({ at })), { width: 1 });
        return;
      }
      if (!drawing || snapped || points.length < 2) return;
      // The weight the line will have, on screen: overlay widths are screen pixels and the
      // weight is in units, so the preview was thinner than the result zoomed out and
      // thicker zoomed in (QA 2026-09-25, pencil-fill-brush-F4).
      overlay.curve(points.map((point) => ({ at: point })), { width: Math.max(1, lineWeight(ctx.style.strokeWeight) / ctx.unitsPerPixel) });
    },
  };
}

/** A ring turned to wind the same way as every other, so overlapping rings fill as one. */
function woundOneWay(ring: Point[]): Point[] {
  let twice = 0;
  for (let index = 0; index < ring.length; index++) {
    const a = ring[index]!;
    const b = ring[(index + 1) % ring.length]!;
    twice += a.x * b.y - b.x * a.y;
  }
  return twice < 0 ? [...ring].reverse() : ring;
}

/** The weight a line is drawn at: the one chosen — 0.5 stays 0.5 — or a hairline when none is. */
function lineWeight(weight: number): number {
  return weight > 0 ? weight : 1;
}

/** A ring of points round a place, for showing how wide the brush is. */
function circleAt(at: Point, radius: number, steps = 24): Point[] {
  const out: Point[] = [];
  for (let step = 0; step <= steps; step++) {
    const angle = (step / steps) * Math.PI * 2;
    out.push({ x: at.x + Math.cos(angle) * radius, y: at.y + Math.sin(angle) * radius });
  }
  return out;
}

/** Anchors carried through a matrix, handles and all. An affine map keeps a curve the same curve. */
function mapped(points: readonly PathPoint[], matrix: Matrix): PathPoint[] {
  const move = (handle: readonly [number, number] | undefined): [number, number] | undefined => {
    if (!handle) return undefined;
    const at = applyToPoint(matrix, handle[0], handle[1]);
    return [at.x, at.y];
  };
  return points.map((point) => {
    const at = applyToPoint(matrix, point.x, point.y);
    const arrive = move(point.in);
    const leave = move(point.out);
    const { in: _in, out: _out, ...rest } = point;
    return { ...rest, x: at.x, y: at.y, ...(arrive ? { in: arrive } : {}), ...(leave ? { out: leave } : {}) };
  });
}

/**
 * The artboard a node is drawn in — the nearest artboard above it — or null on the page.
 * Artboards clip what they hold, so this is what decides where a shape can be seen.
 */
function artboardOf(ctx: ToolContext, id: string): string | null {
  for (const above of ctx.scene.ancestors(id)) if (ctx.scene.get(above)?.type === 'artboard') return above;
  return null;
}

/**
 * Lays down a painted stroke as the SHAPE it covers, merged into whatever it touches.
 *
 * A fill brush is not a line wearing a thick stroke: it is the outline of the sweep, a
 * closed shape you can fill, cut and reshape like any other. The two look identical until
 * you try to edit one, which is the whole reason the tool exists.
 *
 * Painting over earlier paint of the same colour makes ONE shape, not two stacked. That
 * matters more than it sounds: stacked pieces look merged until you select them, and then
 * you find every stroke's own outline still there with its own anchors, which is not a
 * thing anybody can edit. The union is computed exactly by the renderer's path arithmetic
 * rather than approximated here — and the same call folds away the places the offset
 * crossed itself, which is what an outline does wherever the brush is wider than the turn.
 */
function blob(ctx: ToolContext, points: readonly StrokePoint[], radius: number, smoothing: number): boolean {
  // Thinned BEFORE it is outlined, not after. Every sample the screen reported becomes
  // four points of outline and a corner to round, so two hundred samples made an outline
  // of thousands — a shape that draws slowly, saves slowly, and cannot be edited by hand.
  // `smoothing` is already measured on the screen; a floor of one UNIT on top of it
  // straightened every wiggle of a small brush zoomed far in (QA 2026-09-25,
  // pencil-fill-brush-F13).
  const spine = simplifyStroke(points, smoothing * 0.8);
  // The ground the nib covered, piece by piece, rather than one offset ring round the
  // whole line. A ring is only right while the line does not cross itself; where it does,
  // it encloses ground the brush never went near and the fill turns that solid.
  const rings = sweptRings(spine, radius);
  const ring = rings.flat();
  if (ring.length < 3) return false;
  const fill = styleFill(ctx.style);
  /**
   * Paint this stroke is the same as: one solid fill of this colour and alpha, at this
   * opacity, with no outline, no effects and nothing else about it. That is what earlier
   * Fill brush strokes look like. It used to be any path whose first fill had the same
   * RGB — so a pen shape with an outline, or a polygon with a shadow, was swallowed and
   * came back as bare 'Paint' on top of everything.
   */
  const sameColour = (id: string) => {
    const node = ctx.node(id) as (NonNullable<ReturnType<typeof ctx.node>> & { effects?: unknown[]; blendMode?: string; strokes?: { visible?: boolean }[] }) | undefined;
    if (node?.type !== 'path' || ctx.scene.lockedHere(id) || ctx.scene.hiddenHere(id)) return false;
    if (node.path?.subpaths?.some((sub) => !sub.closed)) return false;
    if ((node.strokes ?? []).some((paint) => paint.visible !== false)) return false;
    if ((node.effects ?? []).length > 0 || (node.blendMode ?? 'normal') !== 'normal') return false;
    if (Math.abs((node.opacity ?? 1) - ctx.style.opacity) > 0.001) return false;
    const fills = (node.fills ?? []).filter((item) => item.visible !== false);
    const paint = fills.length === 1 ? fills[0] : null;
    const colour = paint?.type === 'solid' ? paint.color : null;
    return (
      !!colour &&
      Math.abs(colour.r - fill.r) < 0.004 &&
      Math.abs(colour.g - fill.g) < 0.004 &&
      Math.abs(colour.b - fill.b) < 0.004 &&
      Math.abs((colour.a ?? 1) - (fill.a ?? 1)) < 0.004
    );
  };

  // What the sweep landed on: paint of the same colour, which is what "the same blob"
  // means. Measured from the ring rather than from a node that does not exist yet.
  //
  // Only paint on the artboard the stroke starts on, which is where a stroke that merged
  // with nothing lands too. A merge used to re-home everything it swallowed into one
  // parent, so a stroke begun just off an artboard carried the artboard's paint out of it
  // — out of its layers and its export — and one begun inside pulled paint beside the
  // artboard in, cut off at the edge (QA 2026-09-25, pencil-fill-brush-F3).
  const home = ctx.artboardAt(points[0]!) ?? null;
  const box = boundsOfPoints(ring);
  const touching = box ? ctx.nearby(box).filter((id) => sameColour(id) && artboardOf(ctx, id) === home) : [];
  let answer = ctx.unite(rings, touching);
  if (!answer || answer.subpaths.length === 0) return false;
  const order = ctx.scene.paintOrder();
  const topOf = (ids: readonly string[]) => [...ids].sort((a, b) => order.indexOf(b) - order.indexOf(a))[0];
  // Merged into earlier paint, it takes the place of the topmost piece it swallowed —
  // its parent, its spot in the stack, its name — rather than landing on top of the
  // whole artboard. Pieces in a different group on the same artboard stay where they
  // are rather than being pulled into that one, so the union is taken again without them.
  let top = topOf(answer.merged);
  if (top) {
    const group = ctx.scene.parentOf(top);
    if (answer.merged.some((id) => ctx.scene.parentOf(id) !== group)) {
      answer = ctx.unite(rings, answer.merged.filter((id) => ctx.scene.parentOf(id) === group));
      if (!answer || answer.subpaths.length === 0) return false;
      top = topOf(answer.merged);
    }
  }
  const { subpaths: united, merged } = answer;
  const topParent = top ? ctx.scene.parentOf(top) : undefined;
  const parent = top ? (topParent === ROOT_ID ? null : (topParent ?? null)) : home;
  // Through the parent's whole transform, so paint inside a rotated group lands where it was painted.
  const placed = placeInParent(united, parent ? ctx.scene.worldTransform(parent) : null);
  const made = ctx.edit([
    {
      op: 'create',
      ref: 'blob',
      parentId: parent ?? ROOT_ID,
      ...(top ? { position: { before: top } } : {}),
      node: {
        type: 'path',
        name: (top && ctx.node(top)?.name) || 'Paint',
        opacity: ctx.style.opacity,
        transform: placed.transform,
        path: { subpaths: placed.subpaths },
        fills: [{ type: 'solid', color: fill }],
        strokes: [],
      },
    },
    // The paint it grew out of is IN it now, so leaving it behind would be the same shape
    // twice — invisible until somebody moved one of them.
    ...merged.map((id) => ({ op: 'delete', id })),
  ]);
  if (made.ok) ctx.setSelection([made.created.blob!]);
  // Refused — nearly always for lying past the page's coordinate limit — said rather than
  // dropped, so the tool does not look broken (QA 2026-09-25, pencil-fill-brush-F18).
  else ctx.notice(NOT_PLACED);
  return made.ok;
}

const NOT_PLACED = 'That stroke is too far out on the page to be placed.';

/** Puts the shape on the page: one node, one undo step, whatever the stroke turned out to be. */
function commit(ctx: ToolContext, guess: Guess, points: readonly StrokePoint[], smoothing = 1.2): string | null {
  const parent = ctx.artboardAt(points[0]!);
  const back = parent ? invert(ctx.scene.worldTransform(parent)) : null;
  const style = ctx.style;
  // A line needs an outline to be seen at all, so it keeps a hairline when the weight is
  // none — and 0.5 is drawn as 0.5, not raised to 1 (QA 2026-09-25, pencil-fill-brush-F4).
  const stroke = {
    strokes: [{ type: 'solid', color: styleStroke(style) }],
    strokeStyle: { weight: lineWeight(style.strokeWeight) },
  };
  /** A closed shape's outline, which follows the weight like a rectangle's (pencil-fill-brush-F5). */
  const outline = style.strokeWeight > 0 ? { strokes: [{ type: 'solid', color: styleStroke(style) }], strokeStyle: { weight: style.strokeWeight } } : { strokes: [] };
  /**
   * Where the node sits, and what its own coordinates are measured from: through the
   * inverse of the artboard's whole transform, so a turned or scaled board takes it where
   * it was drawn — only the board's offset used to come off (same class as pen-F16).
   */
  const place = (at: Point) => (back ? multiply(back, [1, 0, 0, 1, at.x, at.y]) : [1, 0, 0, 1, at.x, at.y]);
  const local = (at: Point, from: Point) => ({ x: at.x - from.x, y: at.y - from.y });

  const node = (() => {
    switch (guess.kind) {
      case 'ellipse':
      case 'rect': {
        const { box } = guess;
        return {
          type: guess.kind,
          name: guess.kind === 'rect' ? 'Rectangle' : 'Ellipse',
          width: Math.max(1, box.width),
          height: Math.max(1, box.height),
          transform: place({ x: box.x, y: box.y }),
          fills: style.fillOn ? [{ type: 'solid', color: styleFill(style) }] : [],
          ...(style.strokeWeight > 0 ? outline : {}),
        };
      }
      case 'line': {
        return {
          type: 'path',
          name: 'Line',
          transform: place(guess.from),
          path: {
            subpaths: [
              { closed: false, points: [{ x: 0, y: 0, kind: 'corner' }, { ...local(guess.to, guess.from), kind: 'corner' }] },
            ],
          },
          fills: [],
          ...stroke,
        };
      }
      case 'polygon': {
        const first = guess.points[0]!;
        return {
          type: 'path',
          name: guess.closed ? 'Polygon' : 'Path',
          transform: place(first),
          path: {
            subpaths: [
              { closed: guess.closed, points: guess.points.map((point) => ({ ...local(point, first), kind: 'corner' })) },
            ],
          },
          fills: guess.closed && style.fillOn ? [{ type: 'solid', color: styleFill(style) }] : [],
          // Closed, it is a shape like the snapped rectangle and circle and follows the same
          // outline rule; a triangle used to get a thin black edge they did not
          // (QA 2026-09-25, pencil-fill-brush-F5). Open, it is a line and needs one.
          ...(guess.closed ? outline : stroke),
        };
      }
      default: {
        // Kept as drawn, only thinned: a few hundred samples become a few dozen anchors
        // that pass through the same places, which is what makes it an editable path
        // rather than a recording.
        const first = points[0]!;
        const thinned = simplifyStroke(points, smoothing * ctx.unitsPerPixel);
        const subpath = strokeToSubpath(thinned.map((point) => local(point, first)));
        return { type: 'path', name: 'Drawing', transform: place(first), path: { subpaths: [subpath] }, fills: [], ...stroke };
      }
    }
  })();

  const result = ctx.edit([{ op: 'create', ref: 'drawn', parentId: parent ?? ROOT_ID, node: { ...node, opacity: style.opacity } }]);
  if (result.ok && result.created.drawn) {
    ctx.setSelection([result.created.drawn]);
    return result.created.drawn;
  }
  // Said, not dropped (pencil-fill-brush-F18).
  ctx.notice(NOT_PLACED);
  return null;
}
